import { External } from '../../external';

const BASE_URL = `https://${External.serviceApiDomain}/comment/admin`;

export class HttpError extends Error {
  constructor(message, statusCode, response) {
    super(message);
    this.name = 'HttpError';
    this.statusCode = statusCode;
    this.response = response;
  }
}

const getPaginationQueryParams = (timestamp, limit, order) => {
  const searchParams = new URLSearchParams();

  if (timestamp) {
    searchParams.append('timestamp', timestamp);
  }
  if (limit) {
    searchParams.append('limit', limit);
  }
  if (order) {
    searchParams.append('order', order);
  }

  return searchParams.toString();
};

const ProductReviewService = {
  getCurrentAuthor: () => {
    return {
      memberId: -1 * External.userId,
      name: External.userName
    };
  },
  isCommentBelongToAdmin: (comment) => {
    const currentAuthor = ProductReviewService.getCurrentAuthor();
    return currentAuthor.memberId === comment.author.memberId;
  },
  getDefaultContext: async () => {
    const url = `${BASE_URL}/context/default?resourceType=product_review`;
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });

    if (!response.ok) {
      const errorResponse = await response.text().catch(() => '');
      throw new HttpError(`Service Error ${response.status}: ${errorResponse}`, response.status, errorResponse);
    }

    return await response.json();
  },
  createDefaultContext: async () => {
    const url = `${BASE_URL}/context/default`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      },
      body: JSON.stringify({
        resourceType: 'product_review',
        moderation: { writePermission: 2, approvalRequired: 2 },
        commentModeration: { writePermission: 2, approvalRequired: 2 },
        extra: {
          allowMentions: false,
          allowImages: true
        }
      })
    });
    return await response.json();
  },
  partialUpdateContext: async (partialUpdate) => {
    const url = `${BASE_URL}/context/default`;
    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      },
      body: JSON.stringify({
        resourceType: 'product_review',
        ...partialUpdate
      })
    });
    return await response.json();
  },
  getContexts: async (resourceType) => {
    const url = `${BASE_URL}/context?resourceType=${resourceType}`;
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });
    return await response.json();
  },
  getAllRootCommentsByOnlyResourceType: async (resourceType, timestamp, limit, order) => {
    const url = `${BASE_URL}/${resourceType}?${getPaginationQueryParams(timestamp, limit, order)}`;
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });
    return await response.json();
  },
  getAllRootCommentsByResource: async (resourceType, resourceId, timestamp, limit, order) => {
    const url = `${BASE_URL}/${resourceType}/${resourceId}?${getPaginationQueryParams(timestamp, limit, order)}`;
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });
    return await response.json();
  },
  getUnapprovedCommentsByOnlyResourceType: async (resourceType, timestamp, limit, order) => {
    const url = `${BASE_URL}/${resourceType}/waiting-approval?${getPaginationQueryParams(timestamp, limit, order)}`;
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });
    return await response.json();
  },
  getUnapprovedCommentsByResource: async (resourceType, resourceId, timestamp, limit, order) => {
    const url = `${BASE_URL}/${resourceType}/${resourceId}/waiting-approval?${getPaginationQueryParams(timestamp, limit, order)}`;
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });
    return await response.json();
  },
  deleteComment: async (commentId) => {
    const url = `${BASE_URL}/${commentId}`;
    const response = await fetch(url, {
      method: 'DELETE',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });

    return response.ok;
  },
  getAdminAuthor: async () => {
    const url = `${BASE_URL}/admin-author`;
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });
    return await response.json();
  },
  updateAdminAuthor: async (name, extra) => {
    const url = `${BASE_URL}/admin-author`;
    const response = await fetch(url, {
      method: 'PATCH',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      },
      body: JSON.stringify({
        name: name,
        extra: extra
      })
    });

    return await response.json();
  },
  getAllCommentsByParent: async (parentId, timestamp, limit, order) => {
    const url = `${BASE_URL}/p/${parentId}?${getPaginationQueryParams(timestamp, limit, order)}`;
    const response = await fetch(url, {
      method: 'GET',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });
    return await response.json();
  },
  createCommentByParent: async (parentId, content, attachments) => {
    const url = `${BASE_URL}/${parentId}`;
    const response = await fetch(url, {
      method: 'POST',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      },
      body: JSON.stringify({
        content,
        attachments
      })
    });
    return await response.json();
  },
  approveComment: async (commentId) => {
    const url = `${BASE_URL}/${commentId}/approve`;
    const response = await fetch(url, {
      method: 'PATCH',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });

    return response.ok;
  },
  updateComment: async (commentId, content, attachments) => {
    const url = `${BASE_URL}/${commentId}`;
    const response = await fetch(url, {
      method: 'PATCH',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      },
      body: JSON.stringify({
        content: content,
        attachments: attachments
      })
    });

    return await response.json();
  },
  giveReaction: async (commentId, reaction) => {
    const url = `${BASE_URL}/${commentId}/react/${reaction}`;
    await fetch(url, {
      method: 'POST',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });
  },
  removeReaction: async (commentId) => {
    const url = `${BASE_URL}/${commentId}/react`;
    await fetch(url, {
      method: 'DELETE',
      cache: 'no-cache',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'X-Website-Hash': External.WebPlatform.hash,
        'X-Admin-Hash': External.Website.hash
      }
    });
  }
};
export default ProductReviewService;
